import type { ChangeEvent, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFlag } from '@innovamat/flags';
import { snack } from '@innovamat/glimmer-components';
import {
  useChangePasswordMutation,
  useSettingsQuery,
} from '@innovamat/glow-api-client';
import { getRegisterLanguagesByRegion } from '@innovamat/localization';
import { isEmpty, roles, validators } from '@innovamat/radiance-utils';

import {
  Status,
  usePasswordExpirationStatus,
} from '../hooks/use-password-expiration-status';
import { useSetSettings } from '../hooks/use-set-settings';
import type {
  EditSettingsValues,
  SettingsTabType,
  SettingsValues,
  User,
} from '../types';
import { getAvailableSettingsTabs } from '../types';

type MySettingsValue = {
  allPositions?: string[];
  confirmPassword: string;
  contentDrawerIsOpen: boolean;
  country: string | undefined;
  filteredLangs: { value: string; label: string; key: string }[];
  isLoading: boolean;
  isPageLoading: boolean;
  isPositionsDisabled: boolean;
  isSavingSettings: boolean;
  isSubmitEnabled: boolean;
  isSuccessSettings: boolean;
  language: string;
  password: string;
  phoneCode: string | undefined;
  schoolName: string | undefined;
  settingsValues: {
    firstName: string;
    lastName: string;
    phoneNumber: string | null;
  };
  showDrawer: boolean;
  showLanguageSelector: boolean;
  showModalSave: boolean;
  userEmail: string | undefined;
  isLeadOrParent: boolean;
  changeActiveTab: (newTab: SettingsTabType) => void;
  getChecked: (key: string) => boolean;
  getConfirmPasswordErrorMessage: () => string | undefined;
  getCurrentTitle: () => string;
  getTitle: (tab: SettingsTabType) => string;
  handleChangeCheckbox: (role: string) => void;
  handleChangeConfirmPassword: (
    e: ChangeEvent<HTMLInputElement>
  ) => Promise<void>;
  handleChangeLang: (value: string) => void;
  handleChangePassword: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleInputPhone: (e: any, country: any) => void;
  isConfirmPasswordValid: (_confirmPassword: string) => boolean;
  isPasswordValid: (_password: string) => boolean;
  isPhoneValid: (phone: any, phoneDial: any) => boolean;
  isTabActive: (tab: SettingsTabType) => boolean;
  isValueEmpty: (value?: any) => boolean;
  onCancel: () => void;
  onCloseContentDrawer: () => void;
  onCloseModal: () => void;
  onGoBack: () => void;
  onSaveChanges: () => void;
  updateField: (
    name: 'firstName' | 'lastName' | 'phoneNumber',
    value: string
  ) => void;
  managerUrl?: string;
  availableTabs: SettingsTabType[];
  activeTab: SettingsTabType;
  allowDeleteAccount: boolean;
};

const MySettingsContext = createContext<MySettingsValue | undefined>(undefined);

type MySettingsProviderProps = {
  children: ReactNode;
  user: User;
  goBack: () => void;
  onSuccess: () => void;
  initialTab?: SettingsTabType;
};

export function MySettingsProvider({
  children,
  user,
  goBack,
  onSuccess,
  initialTab,
}: MySettingsProviderProps): JSX.Element {
  const { t } = useTranslation();
  const isStudent = roles.hasStudentRole(user.roles);
  const isLeadOrParent =
    roles.hasParentRole(user.roles) || roles.hasLeadRole(user.roles);
  const { data: settingsData } = useSettingsQuery();
  const settings = settingsData?.settings as SettingsValues | undefined;
  const { mutate: changePassword, isPending: isChangePasswordPending } =
    useChangePasswordMutation();
  const { setSettings, isSavingSettings, isSuccessSettings } = useSetSettings({
    onSuccess,
  });
  const { region: operativeRegion, physicalRegion } = user || {};
  const userData = user;
  const { status, refetch } = usePasswordExpirationStatus();

  const { value: showDeletionAccountFlag } = useFlag(
    'showDeletionAccount',
    false,
    {
      identifier: '',
      custom: {
        schoolId: user?.organizationId ?? '',
      },
    }
  );

  const allowDeleteAccount =
    showDeletionAccountFlag &&
    (roles.hasParentRole(user.roles) || roles.hasTeacherRole(user.roles));

  const getShowLanguageSelector = (): boolean => {
    if (isStudent) return false;
    if (operativeRegion === 'MX-PRF') return true;
    return Boolean(operativeRegion && operativeRegion === physicalRegion);
  };

  const showLanguageSelector = getShowLanguageSelector();
  const region = operativeRegion || physicalRegion;

  const [settingsValues, setSettingsValues] = useState<{
    firstName: string;
    lastName: string;
    phoneNumber: string | null;
  }>({ firstName: '', lastName: '', phoneNumber: '' });

  const [language, setLanguage] = useState('');
  const [positions, setPositions] = useState<string[]>();
  const allPositions = settings?.schoolPositions;
  const initialPositions = useMemo(
    () => (settings ? settings.positions : []),
    [settings]
  );
  const [phoneCode, setPhoneCode] = useState();
  const filteredLangs = getRegisterLanguagesByRegion(region)?.map((lang) => ({
    value: lang,
    label: t(`language.${lang}`),
    key: lang,
  }));

  const [someFieldChanged, setSomeFieldChanged] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [password, setPassword] = useState('');
  const [isPageLoading, setIsPageLoading] = useState(true);

  const isPasswordExpired = status === Status.EXPIRED;

  const availableTabs = getAvailableSettingsTabs({
    isLeadOrParent,
    isPasswordExpired,
    showLanguageSelector,
  });

  const [activeTab, setActiveTab] = useState<SettingsTabType>(
    availableTabs.includes(initialTab as SettingsTabType)
      ? (initialTab as SettingsTabType)
      : availableTabs[0]
  );
  useEffect(() => {
    if (!availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [availableTabs.join(','), activeTab]);

  function updateField(
    name: keyof typeof settingsValues,
    value: string | null
  ) {
    setSettingsValues((prevValues) => ({ ...prevValues, [name]: value }));
  }

  function handleInputPhone(e: any, country: any) {
    if (country.dialCode !== phoneCode) setPhoneCode(country.dialCode);
    if (e.length === country.dialCode.length) updateField('phoneNumber', null);
    else updateField('phoneNumber', `+${e}`);
    setSomeFieldChanged(true);
  }

  function handleChangeLang(value: string) {
    setLanguage(value);
  }

  function getChecked(key: string) {
    return positions?.includes(key) || false;
  }

  function handleChangeCheckbox(role: string) {
    setPositions((prevPositions) =>
      prevPositions?.includes(role)
        ? prevPositions.filter((p) => p !== role)
        : [...(prevPositions || []), role]
    );
  }

  const isPositionsDisabled =
    positions?.length === 0 && initialPositions.length === 0;

  function isPasswordValid(_password: string) {
    if (_password.length === 0) return true;
    return /^\S*(?=\S{6,})(?=\S*[a-zA-Z])(?=\S*[\d])\S*$/.test(_password);
  }

  function isConfirmPasswordValid(_confirmPassword: string) {
    if (password.length === 0 && _confirmPassword.length === 0) return true;
    return password === _confirmPassword;
  }

  function getConfirmPasswordErrorMessage() {
    if (!isPasswordValid(confirmPassword))
      return t('settings.change_password.error');
    if (!isConfirmPasswordValid(confirmPassword))
      return t('settings.change_password.not_match');
    return undefined;
  }

  function hasPasswordChangedAndIsValid() {
    return (
      password.length !== 0 &&
      confirmPassword.length !== 0 &&
      isPasswordValid(password) &&
      isConfirmPasswordValid(confirmPassword)
    );
  }

  async function handleChangePassword(e: ChangeEvent<HTMLInputElement>) {
    setPassword(e.target.value);
  }

  async function handleChangeConfirmPassword(e: ChangeEvent<HTMLInputElement>) {
    setConfirmPassword(e.target.value);
  }

  async function handleChangePasswordRequest(
    password: string,
    onlyPasswordHasChanged: boolean
  ) {
    if (userData && userData.id) {
      const userId = userData.id;
      changePassword(
        { id: userId, password },
        {
          onSuccess: () => {
            if (onlyPasswordHasChanged) {
              snack.success(t("Settings.Els canvis s'han desat correctament."));
              refetch();
            }
          },
          onError: (error) => {
            snack.error(t('settings.password-error-conflict'));
          },
          onSettled: () => {
            setPassword('');
            setConfirmPassword('');
          },
        }
      );
    }
  }

  function hasEmptyFields() {
    const { firstName, lastName } = settingsValues;
    return (
      isEmpty(firstName) ||
      isEmpty(lastName) ||
      isEmpty(language) ||
      (isEmpty(positions) && initialPositions.length !== 0)
    );
  }

  function checkEnableButtons() {
    if (!validators.isPhoneValid(settingsValues.phoneNumber, phoneCode))
      return false;
    if (hasEmptyFields()) return false;
    return true;
  }

  function onCancel() {
    goBack();
  }

  function onSaveChanges() {
    if (!settings) return;
    setSomeFieldChanged(false);
    const newSettings = {
      firstName: settingsValues.firstName,
      lastName: settingsValues.lastName,
      phoneNumber: settingsValues.phoneNumber,
      positions: positions,
    };
    const newSettingsValues = { settings: newSettings, language };
    const onlyPasswordHasChanged =
      settings.firstName === newSettings.firstName &&
      settings.lastName === newSettings.lastName &&
      settings.phoneNumber === newSettings.phoneNumber &&
      JSON.stringify(initialPositions) === JSON.stringify(positions) &&
      userData?.locale === language;
    if (hasPasswordChangedAndIsValid())
      handleChangePasswordRequest(password, onlyPasswordHasChanged);
    if (onlyPasswordHasChanged) return;
    setSettings(newSettingsValues as EditSettingsValues);
  }

  useEffect(() => {
    if (!settings || !userData) setIsPageLoading(true);
    if (settings && userData) {
      setSettingsValues({
        firstName: settings.firstName || '',
        lastName: settings.lastName || '',
        phoneNumber: settings.phoneNumber || null,
      });
      setLanguage(userData.locale || '');
      setPositions(settings.positions);
      setIsPageLoading(false);
    }
  }, [settings, userData]);

  useEffect(() => {
    setSomeFieldChanged(
      JSON.stringify(positions) !== JSON.stringify(initialPositions) ||
        settingsValues.firstName !== settings?.firstName ||
        settingsValues.lastName !== settings?.lastName ||
        settingsValues.phoneNumber !== settings?.phoneNumber ||
        userData?.locale !== language
    );
  }, [
    settingsValues,
    language,
    positions,
    initialPositions,
    settings,
    userData,
  ]);

  const isLoading = isSavingSettings || isChangePasswordPending;

  const [contentDrawerIsOpen, setContentDrawerOpen] = useState<boolean>(false);
  const [showDrawer, setShowDrawer] = useState<boolean>(false);

  function getTitle(tab: SettingsTabType): string {
    switch (tab) {
      case 'PersonalData':
        return t('settings.personalData');
      case 'Password':
        return t('settings.change_password');
      case 'Email':
        return t('settings.accounts_connected.title');
      case 'SchoolData':
        return t('settings.SchoolData');
      case 'Language':
        return t('Settings.Language');
    }
  }

  function getCurrentTitle() {
    return getTitle(activeTab);
  }

  function isTabActive(tab: string): boolean {
    return activeTab === tab;
  }

  function changeActiveTab(newTab: SettingsTabType): void {
    setContentDrawerOpen(true);
    setShowDrawer(true);
    if (availableTabs.includes(newTab)) setActiveTab(newTab);
  }

  function onCloseContentDrawer() {
    setContentDrawerOpen(false);
  }

  const [showModalSave, setShowModalSave] = useState<boolean>(false);

  function onGoBack(): void {
    if (!someFieldChanged && !hasPasswordChangedAndIsValid()) {
      goBack();
    }

    setShowModalSave(hasPasswordChangedAndIsValid() || someFieldChanged);
  }

  function onCloseModal(): void {
    setShowModalSave(false);
  }

  const value: MySettingsValue = {
    schoolName: userData?.organizationName,
    userEmail: userData?.email,
    showLanguageSelector,
    settingsValues,
    phoneCode,
    filteredLangs,
    country: region?.toLowerCase(),
    language,
    password,
    confirmPassword,
    allPositions,
    isPageLoading,
    isValueEmpty: isEmpty,
    isPhoneValid: validators.isPhoneValid,
    isPasswordValid,
    isConfirmPasswordValid,
    getConfirmPasswordErrorMessage,
    getChecked,
    updateField,
    handleInputPhone,
    handleChangeLang,
    handleChangePassword,
    handleChangeConfirmPassword,
    handleChangeCheckbox,
    onCancel,
    onSaveChanges,
    isSubmitEnabled:
      checkEnableButtons() &&
      !isLoading &&
      (someFieldChanged || hasPasswordChangedAndIsValid()),
    isLoading,
    changeActiveTab,
    isTabActive,
    getTitle,
    getCurrentTitle,
    showModalSave,
    onCloseModal,
    onGoBack,
    contentDrawerIsOpen,
    onCloseContentDrawer,
    showDrawer,
    isPositionsDisabled,
    isSavingSettings,
    isSuccessSettings,
    isLeadOrParent,
    availableTabs,
    activeTab,
    allowDeleteAccount,
  };
  return (
    <MySettingsContext.Provider value={value}>
      {children}
    </MySettingsContext.Provider>
  );
}

export function useMySettings(): MySettingsValue {
  const context = useContext(MySettingsContext);
  if (context === undefined) {
    throw new Error('useMySettings must be used within a MySettingsProvider');
  }
  return context;
}
